// Créditos del constructor: el único consumo que crece con el uso y no con el número de negocios. El costo de servir
// (§14) contaba horas del equipo; esto cuenta la otra mitad. Todo se habla en créditos, también acá: cuántos
// tokens vale un crédito es una sola cifra, y se ajusta desde esta pantalla mirando el consumo real.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Cargando, Input, Modal, PageHead, Select, Tabla, TarjetaIndicador, Vacio } from '@glubi/ds';
import { api, mensajeDeError } from '../lib/api';
import { momento } from '../lib/formato';
import { useSesion } from '../lib/sesion';

const miles = new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 });

export function Creditos() {
  const q = useQuery({ queryKey: ['panel-creditos'], queryFn: () => api<any>('/api/panel/creditos') });
  const [cupo, setCupo] = useState<any>(null);
  const [equivalencia, setEquivalencia] = useState(false);

  if (q.isLoading || !q.data) return <Cargando />;
  const { periodo, consumo, resumen, programa, cupos } = q.data;
  const mes = consumo.filter((f: any) => f.periodo === periodo);
  const antes = consumo.filter((f: any) => f.periodo !== periodo);
  const propios = cupos.filter((c: any) => c.creditos !== null);

  return (
    <div className="page">
      <PageHead
        title="Créditos del constructor"
        subtitle="Lo que cada negocio consume cuando construye, y hasta dónde puede consumir. Un crédito equivale a una cantidad de tokens del modelo, y esa cantidad se pone acá.">
        <Button variant="secondary" icon="key" onClick={() => setEquivalencia(true)}>Equivalencia y cupo</Button>
      </PageHead>

      <div className="kpis">
        <TarjetaIndicador
          label="Créditos del mes"
          value={miles.format(resumen.creditosDelMes)}
          trend={`${periodo} · ${miles.format(resumen.mensajesDelMes)} mensajes en ${resumen.cuentasQueConsumieron} negocios`}
        />
        <TarjetaIndicador
          label="Créditos por mensaje"
          value={resumen.porMensaje === null ? '—' : miles.format(resumen.porMensaje)}
          trend="Promedio del mes · una respuesta puede pedir de una a ocho vueltas del modelo"
        />
        <TarjetaIndicador
          label="Cerca del cupo"
          value={resumen.alLimite}
          tone={resumen.alLimite > 0 ? 'pending' : 'ok'}
          trend="Negocios que llevan 80% o más de sus créditos del mes"
        />
        <TarjetaIndicador
          label="Cupo del programa"
          value={`${miles.format(programa.creditosMes)} créditos`}
          trend={`Un crédito son ${miles.format(programa.tokensPorCredito)} tokens · rige donde no haya cupo propio`}
        />
      </div>

      <div className="eyebrow">Este mes</div>
      {!mes.length ? <Vacio titulo="Nadie usó el constructor este mes">Un mensaje respondido por el guion no llama al modelo y no consume créditos, así que tampoco aparece acá.</Vacio> : (
        <Tabla rowKey="k" rows={mes.map((f: any, i: number) => ({ ...f, k: i }))}
          columns={[
            { key: 'nombre', label: 'Negocio' },
            { key: 'creditos', label: 'Créditos', numeric: true, render: (f: any) => <Consumido fila={f} programa={programa} /> },
            { key: 'mensajes', label: 'Mensajes', numeric: true },
            { key: 'porMensaje', label: 'Por mensaje', numeric: true, render: (f: any) => miles.format(Math.round(f.creditos / Math.max(1, f.mensajes))) },
            { key: 'llamadas', label: 'Vueltas del modelo', numeric: true, render: (f: any) => `${f.llamadas} · ${(f.llamadas / Math.max(1, f.mensajes)).toFixed(1)} por mensaje` },
            { key: 'tokens', label: 'Tokens', numeric: true, render: (f: any) => `${miles.format(f.tokens)}${f.tokens_cacheados > 0 ? ` · ${Math.round((Number(f.tokens_cacheados) / Math.max(1, Number(f.tokens_entrada))) * 100)}% ya cacheado` : ''}` },
          ]} />
      )}

      <div style={{ margin: '14px 0' }}>
        <Button icon="users" variant="secondary" onClick={() => setCupo({ cuentaId: '', creditos: '', motivo: '' })}>Cambiar el cupo de un negocio</Button>
      </div>

      {Boolean(propios.length) && (
        <>
          <div className="eyebrow">Negocios con cupo propio</div>
          <Tabla rowKey="cuentaId" rows={propios}
            columns={[
              { key: 'negocio', label: 'Negocio' },
              { key: 'creditos', label: 'Créditos al mes', numeric: true, render: (c: any) => (c.creditos === 0 ? <Badge state="error">Sin créditos</Badge> : miles.format(c.creditos)) },
              { key: 'accion', label: '', render: (c: any) => <Button variant="ghost" size="sm" onClick={() => setCupo({ cuentaId: c.cuentaId, creditos: String(c.creditos), motivo: '' })}>Cambiar</Button> },
            ]} />
        </>
      )}

      {Boolean(antes.length) && (
        <>
          <div className="eyebrow" style={{ marginTop: 18 }}>Meses anteriores</div>
          <Tabla rowKey="k" rows={antes.map((f: any, i: number) => ({ ...f, k: i }))}
            columns={[
              { key: 'periodo', label: 'Mes' },
              { key: 'nombre', label: 'Negocio' },
              { key: 'creditos', label: 'Créditos', numeric: true, render: (f: any) => miles.format(f.creditos) },
              { key: 'mensajes', label: 'Mensajes', numeric: true },
            ]} />
        </>
      )}

      <p className="muted" style={{ fontSize: 13, marginTop: 18 }}>
        Un crédito son {miles.format(programa.tokensPorCredito)} tokens, entrada y salida sumadas. Cambiar la equivalencia mueve lo que cuestan
        las respuestas nuevas: cada una queda guardada con la equivalencia que regía cuando se consumió, y eso no se recalcula.
        {programa.actualizadaPor ? ` Cambiada por ${programa.actualizadaPor.nombre} · ${momento(programa.actualizadaEn)}` : ''}
      </p>

      {cupo && <CambiarCupo cupo={cupo} setCupo={setCupo} programa={programa} />}
      {equivalencia && <CambiarEquivalencia programa={programa} onClose={() => setEquivalencia(false)} />}
    </div>
  );
}

// Lo que importa mirar de un vistazo: qué tan cerca está del cupo, sin tener que hacer la resta.
function Consumido({ fila, programa }: { fila: any; programa: { creditosMes: number } }) {
  const tope = fila.tope_creditos ?? programa.creditosMes;
  const pct = tope === 0 ? 100 : Math.round((fila.creditos / tope) * 100);
  return <Badge state={pct >= 100 ? 'error' : pct >= 80 ? 'pending' : 'ok'}>{`${miles.format(fila.creditos)} de ${miles.format(tope)}`}</Badge>;
}

function CambiarEquivalencia({ programa, onClose }: { programa: any; onClose: () => void }) {
  const qc = useQueryClient();
  const { aviso } = useSesion();
  const [f, setF] = useState({ tokensPorCredito: String(programa.tokensPorCredito), creditosMes: String(programa.creditosMes) });

  const guardar = useMutation({
    mutationFn: () => api('/api/panel/creditos/programa', {
      cuerpo: { tokensPorCredito: Number(f.tokensPorCredito), creditosMes: Number(f.creditosMes) },
    }),
    onSuccess: () => { aviso('Equivalencia guardada. Rige de la próxima respuesta en adelante.'); onClose(); void qc.invalidateQueries({ queryKey: ['panel-creditos'] }); },
    onError: (e) => aviso(mensajeDeError(e)),
  });

  const valido = Number(f.tokensPorCredito) > 0 && Number(f.creditosMes) >= 0;
  return (
    <Modal
      title="Equivalencia y cupo del programa"
      subtitle="Cuántos tokens vale un crédito, y cuántos créditos al mes trae un negocio que no tenga cupo propio."
      onClose={onClose}
      footer={<><Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button onClick={() => guardar.mutate()} disabled={guardar.isPending || !valido}>Guardar</Button></>}>
      <div className="stack">
        <Input label="Tokens por crédito" value={f.tokensPorCredito} inputMode="numeric" onChange={(e) => setF({ ...f, tokensPorCredito: e.target.value })}
          helper="Entrada y salida sumadas. Se ajusta cuando cambia el modelo o lo que cuesta" />
        <Input label="Créditos al mes por negocio" value={f.creditosMes} inputMode="numeric" onChange={(e) => setF({ ...f, creditosMes: e.target.value })}
          helper="El cupo por defecto. Un negocio con cupo propio no se ve afectado" />
        <div className="aviso-caja">
          Cambiarla no recalcula nada de lo ya consumido: cada respuesta quedó guardada con la equivalencia de su momento.
          Con {miles.format(Number(f.tokensPorCredito) || 1)} tokens por crédito, una respuesta corriente de unos 15.000 tokens
          cuesta {miles.format(Math.max(1, Math.ceil(15000 / (Number(f.tokensPorCredito) || 1))))} créditos.
        </div>
        {guardar.error && <div className="error-caja">{mensajeDeError(guardar.error)}</div>}
      </div>
    </Modal>
  );
}

function CambiarCupo({ cupo, setCupo, programa }: { cupo: any; setCupo: (v: any) => void; programa: { creditosMes: number } }) {
  const qc = useQueryClient();
  const { aviso } = useSesion();
  const cuentas = useQuery({ queryKey: ['panel'], queryFn: () => api<any>('/api/panel') });

  const guardar = useMutation({
    mutationFn: () => api(`/api/panel/cuentas/${cupo.cuentaId}/creditos`, {
      cuerpo: { creditos: cupo.creditos.trim() === '' ? null : Number(cupo.creditos), motivo: cupo.motivo },
    }),
    onSuccess: () => {
      aviso('Cupo cambiado. Queda en la bitácora del negocio.');
      setCupo(null);
      void qc.invalidateQueries({ queryKey: ['panel-creditos'] });
    },
    onError: (e) => aviso(mensajeDeError(e)),
  });

  return (
    <Modal
      title="Cupo de créditos de un negocio"
      subtitle="Vacío deja el cupo del programa. Cero deja la cuenta sin modelo a propósito: la conversación sigue con el guion."
      onClose={() => setCupo(null)}
      footer={<><Button variant="ghost" onClick={() => setCupo(null)}>Cancelar</Button>
        <Button onClick={() => guardar.mutate()} disabled={guardar.isPending || !cupo.cuentaId || cupo.motivo.trim().length < 5}>Cambiar</Button></>}>
      <div className="stack">
        <Select label="Negocio" value={cupo.cuentaId} onChange={(e) => setCupo({ ...cupo, cuentaId: e.target.value })}
          options={[{ value: '', label: 'Elegir' }, ...(cuentas.data?.cuentas ?? []).map((c: any) => ({ value: c.id, label: c.nombre }))]} />
        <Input label="Créditos al mes" value={cupo.creditos} inputMode="numeric" onChange={(e) => setCupo({ ...cupo, creditos: e.target.value })}
          helper={`Vacío: ${miles.format(programa.creditosMes)}, el del programa`} />
        <Input label="Motivo" value={cupo.motivo} onChange={(e) => setCupo({ ...cupo, motivo: e.target.value })}
          helper="Lo lee el administrador del negocio en su bitácora, así que va en sus palabras" />
        {guardar.error && <div className="error-caja">{mensajeDeError(guardar.error)}</div>}
      </div>
    </Modal>
  );
}
